import { isOneOfFilterEntity, isOneOfEntity, hasFilterEntityDefined } from './row-definition-helpers';

export const ENTITIES = {
  PRODUCT: 'Product',
  COLLECTION: 'Collection',
  USER: 'User',
  ALL_BRAND: 'AllBrand'
};

export const FILTER_ENTITY = {
  CURATOR_GROUP: 'CuratorGroup',
  USER: 'User',
  ALL_BRAND: 'AllBrand',
  CATEGORY: 'Category',
  DEPARTMENT: 'Department',
  INDUSTRY: 'Industry'
};

export const SELECTION = {
  FIXED: {
    label: 'Specific ID',
    allowedWhen: definition => hasFilterEntityDefined(definition),
    key: 'fixed'
  },
  PERSONALIZED: {
    label: 'Personalized for user',
    allowedWhen: definition => hasFilterEntityDefined(definition),
    key: 'personalized',
    default: true
  }
  // RECENTLY_ENGAGED: {
  //   label: 'recently engaged results',
  //   allowedWhen: definition => definition.filterEntity !== 'NONE' && isOneOfEntity([ENTITIES.USER, ENTITIES.PRODUCT])(definition),
  //   filterKey: 'recently_engaged'
  // }
};

export const TAB = {
  // Always allowed
  CATEGORIES: {
    label: 'Categories',
    key: 'categories',
    allowedWhen: hasFilterEntityDefined
  },
  DEPARTMENTS: {
    label: 'Departments',
    key: 'departments',
    allowedWhen: hasFilterEntityDefined
  },
  INDUSTRIES: {
    label: 'Industries',
    key: 'industries',
    allowedWhen: hasFilterEntityDefined
  },
  BRANDS: {
    label: 'Brands',
    key: 'brands',
    allowedWhen: hasFilterEntityDefined
  },

  // Conditionally allowed
  LATEST: {
    label: 'Latest',
    key: 'latest',
    allowedWhen: isOneOfFilterEntity([
      FILTER_ENTITY.CURATOR_GROUP,
      FILTER_ENTITY.ALL_BRAND,
      FILTER_ENTITY.CATEGORY,
      FILTER_ENTITY.DEPARTMENT,
      FILTER_ENTITY.USER
    ])
  },
  COLLECTIONS: {
    label: 'Collections',
    key: 'collections',
    allowedWhen: isOneOfFilterEntity([FILTER_ENTITY.CURATOR_GROUP, FILTER_ENTITY.USER])
  },
  POPULAR: {
    label: 'Popular',
    key: 'popular',
    allowedWhen: isOneOfFilterEntity([
      FILTER_ENTITY.CURATOR_GROUP,
      FILTER_ENTITY.ALL_BRAND,
      FILTER_ENTITY.CATEGORY,
      FILTER_ENTITY.DEPARTMENT,
      FILTER_ENTITY.USER
    ]),
    default: true
  },
  POPULAR_MONTHLY: {
    label: 'Popular Monthly',
    key: 'popular-monthly',
    allowedWhen: isOneOfFilterEntity([FILTER_ENTITY.ALL_BRAND, FILTER_ENTITY.CATEGORY, FILTER_ENTITY.DEPARTMENT, FILTER_ENTITY.USER])
  },
  POPULAR_WEEKLY: {
    label: 'Popular Weekly',
    key: 'popular-weekly',
    allowedWhen: isOneOfFilterEntity([FILTER_ENTITY.ALL_BRAND, FILTER_ENTITY.CATEGORY, FILTER_ENTITY.DEPARTMENT, FILTER_ENTITY.USER])
  },
  CURATORS: {
    label: 'Curators',
    key: 'curators',
    allowedWhen: isOneOfFilterEntity([FILTER_ENTITY.CURATOR_GROUP])
  }
};

export const POOL_REFINEMENT = {
  NONE: {
    label: 'Everything',
    allowedWhen: hasFilterEntityDefined,
    key: 'none',
    default: true
  },
  IN_CIRCLE: {
    label: "In user's circle",
    allowedWhen: isOneOfFilterEntity([FILTER_ENTITY.USER, FILTER_ENTITY.CURATOR_GROUP]),
    key: 'in_circle'
  }
};

export const SECTION_TYPE = {
  POSTS: {
    label: 'Posts',
    key: 'posts',
    allowedWhen: isOneOfEntity([ENTITIES.COLLECTION])
  },
  GIFT_GUIDES: {
    label: 'Gift Guides',
    key: 'gift_guides',
    allowedWhen: isOneOfEntity([ENTITIES.COLLECTION])
  }
};

// Circle-scoped CuratorGroup rows resolve as user_curator_groups[circle_index] at generation time;
// without the index the generator silently drops the row for users with fewer circles than rows.
export const requiresCircleIndex = definition =>
  definition.filterEntity === FILTER_ENTITY.CURATOR_GROUP && definition.poolRefinement === POOL_REFINEMENT.IN_CIRCLE.key;

export const isValidCircleIndex = value => `${value ?? ''}`.trim() !== '' && Number.isInteger(Number(value)) && Number(value) >= 0;

export const definitionAsRow = definition => {
  // definition.tab holds an option key (e.g. 'latest'), so match TAB entries by key.
  const knownTab = Object.values(TAB).find(t => t.key === definition.tab);
  const row = {
    filter: {
      entity: definition.filterEntity,
      tab: knownTab ? knownTab.key : TAB.POPULAR.key
    },
    cardSort: null,
    entity: definition.entity || 'Product'
  };

  if (definition.poolRefinement === POOL_REFINEMENT.IN_CIRCLE.key) {
    row.filter.scoped_to_circle = true;
  }

  if (requiresCircleIndex(definition) && isValidCircleIndex(definition.circleIndex)) {
    row.filter.circle_index = Number(definition.circleIndex);
  }

  if (definition.sectionType) {
    row.filter.sectionType = definition.sectionType;
  }

  if (definition.selection === SELECTION.FIXED.key && definition.Entity_id) {
    row.filter.Entity_id = definition.Entity_id;
  }

  return row;
};

// Inverse of definitionAsRow: reconstructs a `definition` object from a saved row's `entity` and `filter`.
export const rowAsDefinition = row => {
  if (!row) return {};
  const filter = row.filter || {};

  let selection = null;
  if (filter.Entity_id != null) {
    selection = SELECTION.FIXED.key;
  } else if (filter.entity) {
    selection = SELECTION.PERSONALIZED.key;
  }

  let poolRefinement = null;
  if (filter.entity) {
    // Legacy rows carry circle_index without scoped_to_circle; the index alone means circle-scoped.
    poolRefinement = filter.scoped_to_circle || filter.circle_index != null ? POOL_REFINEMENT.IN_CIRCLE.key : POOL_REFINEMENT.NONE.key;
  }

  return {
    entity: row.entity || null,
    filterEntity: filter.entity || null,
    tab: filter.tab || null,
    poolRefinement,
    circleIndex: filter.circle_index != null ? Number(filter.circle_index) : null,
    sectionType: filter.sectionType || null,
    selection,
    Entity_id: filter.Entity_id != null ? filter.Entity_id : null
  };
};
